import { useEffect, useRef } from "react";
import { MARQUEE_SIDE_GUTTER_PX } from "./useMarqueeSelection";

/**
 * Fixed hit-targets over the deal-card side gutters (mx-6).
 * Needed because the folder list lives in overflow-y-scroll, so absolute
 * negative offsets are clipped. Positioned imperatively — no React state.
 */
export function MarqueeGutterStrips(props: {
  containerRef: React.RefObject<HTMLElement | null>;
  enabled: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  const { containerRef, enabled, onMouseDown } = props;
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const onMouseDownRef = useRef(onMouseDown);
  onMouseDownRef.current = onMouseDown;
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const left = leftRef.current;
    const right = rightRef.current;
    if (!enabled || !left || !right) {
      if (left) left.style.display = "none";
      if (right) right.style.display = "none";
      return;
    }

    const apply = () => {
      rafRef.current = null;
      const el = containerRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      if (rect.height <= 0) {
        left.style.display = "none";
        right.style.display = "none";
        return;
      }

      left.style.display = "block";
      left.style.top = `${rect.top}px`;
      left.style.height = `${rect.height}px`;
      left.style.left = `${rect.left - MARQUEE_SIDE_GUTTER_PX}px`;
      left.style.width = `${MARQUEE_SIDE_GUTTER_PX}px`;

      right.style.display = "block";
      right.style.top = `${rect.top}px`;
      right.style.height = `${rect.height}px`;
      right.style.left = `${rect.right}px`;
      right.style.width = `${MARQUEE_SIDE_GUTTER_PX}px`;
    };

    const schedule = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(apply);
    };

    apply();

    const scrollParent = containerRef.current?.closest(
      ".overflow-y-scroll, .overflow-y-auto",
    );

    window.addEventListener("resize", schedule, { passive: true });
    scrollParent?.addEventListener("scroll", schedule, { passive: true });

    return () => {
      window.removeEventListener("resize", schedule);
      scrollParent?.removeEventListener("scroll", schedule);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      left.style.display = "none";
      right.style.display = "none";
    };
  }, [enabled, containerRef]);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={leftRef}
        aria-hidden
        className="fixed z-40 hidden cursor-crosshair"
        onMouseDown={(e) => onMouseDownRef.current(e)}
      />
      <div
        ref={rightRef}
        aria-hidden
        className="fixed z-40 hidden cursor-crosshair"
        onMouseDown={(e) => onMouseDownRef.current(e)}
      />
    </>
  );
}
